import type { InsurancePolicy, InsurancePolicyProduct } from 'src/interfaces/insurancePolicy'
import type { InsuranceProduct } from 'src/interfaces/insuranceProduct'

/**
 * In-memory search over a customer's policies — the list endpoint takes no search param, so the
 * client holds the whole set. Matches the policy name and the names of the products inside it,
 * which is what the "Products in policy" column shows.
 *
 * Returns the input array by reference for an empty query so an untouched search box does not
 * churn the caller's `useMemo`.
 */
export const filterInsurancePolicies = (
  policies: InsurancePolicy[],
  search: string,
): InsurancePolicy[] => {
  const normalizedSearch = search.trim().toLowerCase()

  if (normalizedSearch === '') {
    return policies
  }

  return policies.filter((policy) => {
    if (policy.name.toLowerCase().includes(normalizedSearch)) {
      return true
    }

    // `insuranceProduct` is left-joined, so a row whose catalog entry was deleted arrives
    // without one. Optional-chain it rather than assuming the join produced a value.
    return policy.insurancePoliciesProducts.some((policyProduct) => {
      const productName = policyProduct.insuranceProduct?.name ?? ''

      return productName.toLowerCase().includes(normalizedSearch)
    })
  })
}

/**
 * The global catalog minus the products already inside the policy.
 *
 * This is the real guard against the backend's 409 (`Insurance product with ID … already
 * assigned to that policy`): a product that cannot be picked cannot be submitted twice. The
 * 409 stays as a race-condition backstop, not as the primary check.
 *
 * Returns the catalog by reference when nothing is excluded, so the caller's `useMemo` is
 * stable while the admin has no policy selected.
 */
export const selectableInsuranceProducts = (
  catalog: InsuranceProduct[],
  policy: InsurancePolicy | null,
): InsuranceProduct[] => {
  if (!policy || policy.insurancePoliciesProducts.length === 0) {
    return catalog
  }

  const assignedProductIds = new Set(
    policy.insurancePoliciesProducts.map((policyProduct) => {
      return policyProduct.insuranceProductId
    }),
  )

  return catalog.filter((insuranceProduct) => {
    return !assignedProductIds.has(insuranceProduct.id)
  })
}

/**
 * `InsurancePolicyFullDto.insurancePoliciesProducts` is decorated `@Type(() =>
 * InsurancePolicyProductPlainDto)`, and only the Full variant carries `insuranceProduct` — so
 * `plainToInstance(..., { excludeExtraneousValues: true })` strips it from every product on
 * every policy the list endpoint returns, even though the repository left-joined it (ENG-750).
 * This backfills the name from the catalog this page already fetches, so a product's name
 * renders instead of its raw UUID until the backend is fixed.
 *
 * Only fills a product whose nested `insuranceProduct` is ABSENT, never overwrites one that is
 * present — that is what makes this forward-compatible: once ENG-750 ships, every product
 * already carries its own nested value and this helper quietly stops changing anything.
 *
 * Returns the input policy BY REFERENCE when no product needed filling (including when the
 * catalog is empty), so a caller's `useMemo` stays stable.
 */
const resolvePolicyProducts = (
  policy: InsurancePolicy,
  catalog: InsuranceProduct[],
): InsurancePolicy => {
  let didResolveAny = false

  const resolvedProducts = policy.insurancePoliciesProducts.map((policyProduct) => {
    if (policyProduct.insuranceProduct) {
      return policyProduct
    }

    const catalogProduct = catalog.find((insuranceProduct) => {
      return insuranceProduct.id === policyProduct.insuranceProductId
    })

    if (!catalogProduct) {
      return policyProduct
    }

    didResolveAny = true

    return { ...policyProduct, insuranceProduct: catalogProduct }
  })

  if (!didResolveAny) {
    return policy
  }

  return { ...policy, insurancePoliciesProducts: resolvedProducts }
}

export const withResolvedInsuranceProducts = (
  policy: InsurancePolicy | null,
  catalog: InsuranceProduct[],
): InsurancePolicy | null => {
  if (!policy || catalog.length === 0) {
    return policy
  }

  return resolvePolicyProducts(policy, catalog)
}

/**
 * The list variant of `withResolvedInsuranceProducts`, for the policies table. Returns the
 * input array BY REFERENCE when no policy in it needed filling, so the table's `useMemo` stays
 * stable.
 */
export const withResolvedInsuranceProductsForPolicies = (
  policies: InsurancePolicy[],
  catalog: InsuranceProduct[],
): InsurancePolicy[] => {
  if (catalog.length === 0) {
    return policies
  }

  let didResolveAny = false

  const resolvedPolicies = policies.map((policy) => {
    const resolved = resolvePolicyProducts(policy, catalog)

    if (resolved !== policy) {
      didResolveAny = true
    }

    return resolved
  })

  if (!didResolveAny) {
    return policies
  }

  return resolvedPolicies
}

/**
 * Finds a policy product by the GLOBAL product id, never by its own row id.
 *
 * After an `immutable` mutation the policy is cloned, so every product row gets a new `id`
 * while `insuranceProductId` is carried over. Re-seeding an open form by row id would find
 * nothing; by product id it finds the same product on the new version.
 */
export const findPolicyProduct = (
  policy: InsurancePolicy | null,
  insuranceProductId: string | null,
): InsurancePolicyProduct | null => {
  if (!policy || !insuranceProductId) {
    return null
  }

  const found = policy.insurancePoliciesProducts.find((policyProduct) => {
    return policyProduct.insuranceProductId === insuranceProductId
  })

  return found ?? null
}
